import Menu from "./Menu";
import styled from "@emotion/styled";
import { css, keyframes } from '@emotion/react';
import React, {useContext, useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {googleLogout, useGoogleLogin} from "@react-oauth/google";
import axiosInstance, {env} from "./Axios";
import jwtDecode from "jwt-decode";
import {useNavigate} from "react-router-dom";
import {ShakeButtonContext} from "./ShakeButtonContext";
import {ReactComponent as AccountIcon} from "./icons/account_circle_fill1_24px.svg"
import {ReactComponent as StatIcon} from "./icons/stat_3_fill1_24px.svg"
import {useToken} from "./store/TokenContext";
import {ReactComponent as AddCircleIcon} from "./icons/add_circle_24px.svg"
import MenuAdd from "./MenuAdd";

const bounce = keyframes`
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-10px); /* Déplace l'icône vers le haut de 10px */
  }
`;

const AnimatedIconContainer = styled.div`
    position: absolute;
    top: 40px;
    animation: ${bounce} 2s ease-in-out infinite; /* Applique l'animation de rebondissement */
`;

const HeaderContainer = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    position: fixed;
    width: 100vw;
    top: 0;  /* Ajout de cette ligne pour positionner le header au sommet */
    left: 0;
    z-index: 9999;
    background-color: #f2efff;
    height: 48px;
`;
const AddCircleIconStyled = styled(AddCircleIcon)`
    position: relative;
    right: 10px;
    top: 10px;
`;

const shakeAnimation = keyframes`
    0% { transform: translate(1px, 1px) rotate(0deg); }
    10% { transform: translate(-1px, -2px) rotate(-1deg); }
    20% { transform: translate(-3px, 0px) rotate(1deg); }
    30% { transform: translate(3px, 2px) rotate(0deg); }
    40% { transform: translate(1px, -1px) rotate(1deg); }
    50% { transform: translate(-1px, 2px) rotate(-1deg); }
    60% { transform: translate(-3px, 1px) rotate(0deg); }
    70% { transform: translate(3px, 1px) rotate(-1deg); }
    80% { transform: translate(-1px, -1px) rotate(1deg); }
    90% { transform: translate(1px, 2px) rotate(0deg); }
    100% { transform: translate(1px, -2px) rotate(-1deg); }
`;


const StyledButton = styled.button<{ shouldShake?: boolean }>`
  position: relative; // Ajouter ceci pour permettre le positionnement absolu de l'image
  display: flex;  
  align-items: center;  
  justify-content: center;  
  border: none;
  border-radius: 12px;
  cursor: pointer;
  transition-duration: 0.4s;
    background-color: transparent;
    height: 48px;

    @media (max-width: 768px) {
  }

  animation: ${({ shouldShake }) =>
    shouldShake
        ? css`${shakeAnimation} 0.5s linear infinite`
        : "none"};
`;

type UserInfoType = {
    "user_id": string,
    "created_at": string,
    "given_name": string,
    "family_name": string,
    "email": string,
    "picture": string,
    "name": string
}

const Header: React.FC = () => {
    const [userInfo, setUserInfo] = useState<UserInfoType | null>(null);
    const { shouldShakeButton, setShouldShakeButton } = useContext(ShakeButtonContext);  // Extraire à partir du contexte
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { setToken } = useToken();

    useEffect(() => {
        const intervalId = setInterval(refreshAccessToken, 55 * 60 * 1000);
        getUserInfo();
        getCredit();
        return () => clearInterval(intervalId);
    }, []); // Passing an empty array as second argument triggers the callback in useEffect only after the initial render thus replicating `componentDidMount` lifecycle behaviour

    useEffect(() => {
        const storedUserInfo = localStorage.getItem('userInfo');
        if (storedUserInfo) {
            setUserInfo(JSON.parse(storedUserInfo));
        }
    }, []); // Pas de dépendance ici

    const refreshAccessToken = async () => {
        try {
            const refreshToken = localStorage.getItem('refresh_token');
            if (!refreshToken) throw new Error("No refresh token found.");

            let url = 'oauth2/v2/auth';
            if (env === 'dev') {
                url += '/dev';
            }
            if (env === 'help') {
                url += '/help';
            }
            const response = await axiosInstance.post(url, {}, {
                headers: {
                    Authorization: `Bearer ${refreshToken}`,
                },
                params: {
                    type: 'refresh'
                }
            });
            const tokens = response.data;
            localStorage.setItem('token', tokens.id_token);
            setToken(tokens.id_token);
            localStorage.setItem('access_token', tokens.access_token);
            localStorage.setItem('token_expiry', tokens.token_expiry);
            localStorage.setItem('current_time', tokens.current_time);
        } catch (error) {
            console.error("Error refreshing access token:", error);
        }
    };

    const handleSignOut = async () => {
        try {
            await googleLogout();
            localStorage.removeItem('token');
            setToken(null);
            localStorage.removeItem('access_token');
            localStorage.removeItem('refresh_token');
            localStorage.removeItem('token_expiry');
            localStorage.removeItem('current_time');
            localStorage.removeItem('userInfo');
            setUserInfo(null);
            navigate('/');
        } catch (error) {
            console.error("Error during logout:", error);
        }
    };

    const getUserInfo = async () => {
        try {
            type JWTDeCode  = {
                sub: string,
                email: string,
                iat: number,
                exp: number
            }

            const id_token = localStorage.getItem('token');
            if (id_token === null) {
                console.error("No token found in localStorage.");
                return;
            }
            const decodedToken : JWTDeCode = jwtDecode(id_token);
            const user_id = decodedToken.sub;
            const response = await axiosInstance.get('/user-info/' + user_id, {
                headers: {
                    Authorization: `Bearer ${id_token}`,
                },
            })
                .catch((error) => {
                    if (error.response.status === 401) {
                        // Tentez de rafraîchir le token ici, puis refaites la requête
                    }
                });
            if (response) {
                localStorage.setItem('userInfo', JSON.stringify(response.data));
                setUserInfo(response.data);
            }
        } catch (error) {
            console.error("Error calling API Gateway:", error);
        }
    };

    const getCredit = async () => {
        try {
            type JWTDeCode  = {
                sub: string,
                email: string,
                iat: number,
                exp: number
            }

            const id_token = localStorage.getItem('token');
            if (id_token === null) {
                console.error("No token found in localStorage.");
                return;
            }
            const decodedToken : JWTDeCode = jwtDecode(id_token);
            const user_id = decodedToken.sub;
            const response = await axiosInstance.get('/credit/' + user_id, {
                headers: {
                    Authorization: `Bearer ${id_token}`,
                },
            })
                .catch((error) => {
                    if (error.response.status === 401) {
                        // Tentez de rafraîchir le token ici, puis refaites la requête
                    }
                });
            if (response) {
                localStorage.setItem('credit', JSON.stringify(response.data));
                setUserInfo(response.data);
            }
        } catch (error) {
            console.error("Error calling API Gateway:", error);
        }
    };

    const handleSuccess = async (codeResponse: any) => {
        // Envoyez le code d'autorisation au serveur pour échanger contre des tokens d'accès et de rafraîchissement
        try {
            let url = 'oauth2/v2/auth';
            if (env === 'dev') {
                url += '/dev';
            }
            if (env === 'help') {
                url += '/help';
            }
            if (env === 'imlingo') {
                url += '/imlingo';
            }
            const tokenResponse = await axiosInstance.post(url, {}, {
                headers: {
                    Authorization: `Bearer ${codeResponse}`,
                },
                params: {
                    type: 'code'
                }
            });
            const tokens = tokenResponse.data;
            localStorage.setItem('token', tokens.id_token);
            setToken(tokens.id_token);
            localStorage.setItem('access_token', tokens.access_token);
            localStorage.setItem('refresh_token', tokens.refresh_token);
            localStorage.setItem('token_expiry', tokens.token_expiry);
            localStorage.setItem('current_time', tokens.current_time);

            getUserInfo();
            getCredit();
            window.location.reload();
        } catch (error) {
            console.error("Error calling API Gateway:", error);
        }
    };

    const login = useGoogleLogin({
        onSuccess: codeResponse => {
            handleSuccess(codeResponse.code);
        },
        flow: 'auth-code',
    });

    const toHome = () => {
        navigate('/');
    }

    return (
        <HeaderContainer>
            {userInfo ? (
                <Menu userImage={userInfo?.picture ?? null} userName={userInfo?.name ?? null}
                      setUserInfo={setUserInfo} userInfo={userInfo} handleSignOut={handleSignOut}/>
            ) : (
                <StyledButton shouldShake={shouldShakeButton} onClick={() => login()}>
                    <AccountIcon style={{width: '30px', height: '30px', marginTop: '5px'}}/>
                    {t('signInWithGoogle')}
                    <AnimatedIconContainer>
                        <StatIcon/>
                    </AnimatedIconContainer>
                </StyledButton>
            )}
           {/* {userInfo ? <MenuAdd /> : null}*/}
            <img onClick={toHome} src="/images/logo.png" style={{width: '48px', height: '48px', top: '5px', right: '10px', position: 'absolute'}}/>

        </HeaderContainer>
);
};

export default Header;
